import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { getFlag } from "@/config/rdoFlags";
import type { RDO as UnifiedRDO } from "@/domain/rdo/schema";
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WorkforceForRDO } from '@/components/rdo/adapters/WorkforceForRDO';
import { ActivitiesForRDO } from '@/components/rdo/adapters/ActivitiesForRDO';
import { PhotosForRDO } from '@/components/rdo/adapters/PhotosForRDO';
import { VideosForRDO } from '@/components/rdo/adapters/VideosForRDO';
import { EquipmentsForRDO } from '@/components/rdo/adapters/EquipmentsForRDO';
import { EquipmentCardsLayout } from '@/components/rdo/EquipmentCardsLayout';
import { EditEquipmentModal } from '@/components/rdo/EditEquipmentModal';
import { WorkforceIndividualEditModal } from '@/components/rdo/WorkforceIndividualEditModal';
import { WorkforceTeamEditModal } from '@/components/rdo/WorkforceTeamEditModal';
import { MaterialsSection } from "@/components/rdo/MaterialsSection";
import { MaterialsForRDO } from "@/components/rdo/adapters/MaterialsForRDO";
import { CommentsSection } from "@/components/rdo/CommentsSection";
import { CommentsForRDO } from "@/components/rdo/adapters/CommentsForRDO";
import { PhotosSection } from "@/components/rdo/PhotosSection";
import { VideosSection } from "@/components/rdo/VideosSection";
import { AttachmentsSection } from "@/components/rdo/AttachmentsSection";
import { AttachmentsForRDO } from "@/components/rdo/adapters/AttachmentsForRDO";
import { OccurrencesForRDO } from "@/components/rdo/adapters/OccurrencesForRDO";
// Legacy components
import { LegacyWorkforceSection } from "@/components/rdo/legacy/LegacyWorkforceSection";
import { LegacyMaterialsSection } from "@/components/rdo/legacy/LegacyMaterialsSection";
import { LegacyActivitiesSection } from "@/components/rdo/legacy/LegacyActivitiesSection";
import { LegacyPhotosSection } from "@/components/rdo/legacy/LegacyPhotosSection";
import { LegacyVideosSection } from "@/components/rdo/legacy/LegacyVideosSection";
import { LegacyAttachmentsSection } from "@/components/rdo/legacy/LegacyAttachmentsSection";
import { LegacyEquipmentsSection } from "@/components/rdo/legacy/LegacyEquipmentsSection";
import { LegacyCommentsSection } from "@/components/rdo/legacy/LegacyCommentsSection";
import { SignaturePopup } from "@/components/rdo/SignaturePopup";
import { PreencherRelatorioModal } from "@/components/rdo/PreencherRelatorioModal";
import { ExportOptionsModal } from "@/components/rdo/ExportOptionsModal";
import { WeatherIconButton } from "@/components/rdo/WeatherIconButton";
import { DialogAddWorkforce } from "@/components/rdo/DialogAddWorkforce";
import { DialogAddEquipment } from "@/components/rdo/DialogAddEquipment";
import { DialogAddMaterial } from "@/components/rdo/DialogAddMaterial";
import { DialogFotos } from "@/components/rdo/DialogFotos";
import { DialogVideos } from "@/components/rdo/DialogVideos";
import { DialogAnexos } from "@/components/rdo/DialogAnexos";

import { useObraReportDefaults } from "@/hooks/useObraReportDefaults";
import { useViewModePersistence } from '@/hooks/useViewModePersistence';
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import PrintStyles from "@/components/print/PrintStyles";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Toggle } from "@/components/ui/toggle";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { 
  ArrowLeft, 
  Save, 
  Check,
  Plus,
  Trash2,
  Cloud,
  Users,
  Timer,
  FileText,
  Sun,
  MessageSquare,
  Camera,
  Video,
  Paperclip,
  Upload,
  Image,
  FileVideo,
  X,
  Truck,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
  Edit,
  Sparkles,
  FileCheck, 
  Download, 
  Printer, 
  Copy,
  Calendar,
  Clock,
  User,
  Thermometer,
  CloudRain,
  Signature,
  Building,
  MapPin,
  Phone,
  CalendarDays,
  FileSignature,
  CheckCircle,
  Mail,
  Link2,
  ChevronDown,
  Package
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { WeatherCondition } from '@/utils/weatherUtils';
import { getRDOById, getRDOByObraDate, updateRDO, listRDOs } from "@/services/rdoStorageSupabase";
import type { RDO } from "@/services/rdoStorageService";
import { getObraById } from "@/services/obraStorageService";
import { getClienteNomeById } from "@/utils/clientesData";
import { getCurrentUserName, getCurrentUserEmail } from "@/utils/userUtils";
import { parseYmdLocal, formatYmd } from "@/utils/dateUtils";
import { pdfExportService } from "@/services/pdfExportService";
import { useWhatsApp } from "@/hooks/useWhatsApp";

// Fase 1: a normalização para o schema unificado não é mais aplicada ao estado
// da página (ver load abaixo). Conversão ocorre nos adapters por módulo.

interface SignatureFieldProps {
  title: string;
  userName: string;
  isSigned: boolean;
  signatureDate?: string;
  onSign: () => void;
}

const SignatureField = ({ title, userName, isSigned, signatureDate, onSign }: SignatureFieldProps) => {
  return (
    <div className="border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm">{title}</h4>
        <Badge variant={isSigned ? "default" : "secondary"}>
          {isSigned ? "Assinado" : "Pendente"}
        </Badge>
      </div>
      
      {isSigned ? (
        <div className="space-y-2">
          <div className="bg-muted/50 border-2 border-dashed border-primary/30 rounded-lg p-4 h-24 flex items-center justify-center">
            <div className="text-center space-y-1">
              <Signature className="w-6 h-6 mx-auto text-primary" />
              <p className="text-xs font-medium">{userName}</p>
            </div>
          </div>
          <div className="text-xs text-muted-foreground space-y-1">
            <p><span className="font-medium">Assinado por:</span> {userName}</p>
            <p><span className="font-medium">Data/Hora:</span> {signatureDate}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="bg-muted/30 border border-dashed border-muted-foreground/30 rounded-lg p-4 h-24 flex items-center justify-center">
            <div className="text-center space-y-1">
              <User className="w-6 h-6 mx-auto text-muted-foreground" />
              <p className="text-xs text-muted-foreground">{userName}</p>
            </div>
          </div>
          <div className="text-xs text-muted-foreground space-y-1">
            <p><span className="font-medium">Aguardando assinatura de:</span> {userName}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onSign} className="w-full">
            <FileSignature className="w-4 h-4 mr-2" />
            Assinar
          </Button>
        </div>
      )}
    </div>
  );
};

function RDO() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Parameters for loading RDO
  const rdoId = searchParams.get('rdoId') || location.state?.rdoId;
  const obraId = searchParams.get('obra') || location.state?.obraId;
  const dateParam = searchParams.get('date') || location.state?.date;
  
  // RDO data and loading states
  const [rdoData, setRdoData] = useState<RDO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialDataRef = useRef<RDO | null>(null);
  
  // Modal states - matching PreenchendoRDO.tsx
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isNewRDOModalOpen, setIsNewRDOModalOpen] = useState(false);
  const [isExportOptionsModalOpen, setIsExportOptionsModalOpen] = useState(false);
  const [pendingExportType, setPendingExportType] = useState<'pdf' | 'whatsapp' | 'email' | 'link'>('pdf');
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  
  // Edit form modal states - matching PreenchendoRDO.tsx exactly
  const [showWorkforceModal, setShowWorkforceModal] = useState(false);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [showEditEquipmentModal, setShowEditEquipmentModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showActivityPhotoModal, setShowActivityPhotoModal] = useState(false);
  const [showActivityGalleryModal, setShowActivityGalleryModal] = useState(false);
  const [selectedActivityIndex, setSelectedActivityIndex] = useState<number | null>(null);
  const [selectedActivityFiles, setSelectedActivityFiles] = useState<FileList | null>(null);
  const [showOccurrencePhotoModal, setShowOccurrencePhotoModal] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");
  const [selectedOccurrenceIndex, setSelectedOccurrenceIndex] = useState<number | null>(null);
  const [selectedOccurrenceFiles, setSelectedOccurrenceFiles] = useState<FileList | null>(null);
  const [showOccurrenceGalleryModal, setShowOccurrenceGalleryModal] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showCaptionEditModal, setShowCaptionEditModal] = useState(false);
  const [editingCaptionIndex, setEditingCaptionIndex] = useState<number | null>(null);
  const [editingCaptionType, setEditingCaptionType] = useState<'photo' | 'video' | null>(null);
  const [editingCaptionText, setEditingCaptionText] = useState("");
  const [videoViewerOpen, setVideoViewerOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [editingEquipmentIndex, setEditingEquipmentIndex] = useState<number | null>(null);
  const { getPreference, updatePreference } = useViewModePersistence();
  const [workforceViewMode, setWorkforceViewMode] = useState<'cards' | 'table'>(() => 
    getPreference('workforce', 'cards')
  );
  const [editingWorkforceIndex, setEditingWorkforceIndex] = useState<number | null>(null);
  
  // Estados para controlar modais de edição específicos - matching PreenchendoRDO.tsx
  const [showIndividualEditModal, setShowIndividualEditModal] = useState(false);
  const [showTeamEditModal, setShowTeamEditModal] = useState(false);
  const [editingWorkforceData, setEditingWorkforceData] = useState<any>(null);
  
  // Load report defaults for this obra
  const { reportDefaults, isLoading: isLoadingDefaults, error: defaultsError } = useObraReportDefaults(obraId || '');
  const obra = getObraById(obraId || '');
  const { openWhatsApp } = useWhatsApp();

  // Navegação entre RDOs da obra (lista ordenada por data, desc).
  // "Anterior" = mais antigo (idx+1); "Próximo" = mais novo (idx-1).
  const [prevSibling, setPrevSibling] = useState<RDO | null>(null);
  const [nextSibling, setNextSibling] = useState<RDO | null>(null);
  useEffect(() => {
    let active = true;
    (async () => {
      if (!obraId || !rdoData?.id) { setPrevSibling(null); setNextSibling(null); return; }
      const list = await listRDOs(obraId);
      if (!active) return;
      const idx = list.findIndex((r) => r.id === rdoData.id);
      if (idx === -1) { setPrevSibling(null); setNextSibling(null); return; }
      setPrevSibling(list[idx + 1] ?? null);
      setNextSibling(list[idx - 1] ?? null);
    })();
    return () => { active = false; };
  }, [obraId, rdoData?.id]);

  // Permissão de aprovação por obra (aprovadores em obra.aprovacao.participantes,
  // persistidos em obras.signature_settings). Sem aprovadores configurados =>
  // liberado (compatibilidade). A identidade vem do profile do usuário logado
  // (getCurrentUserName, hidratado no login); a comparação é por nome.
  const obraApprovers = obra?.aprovacao?.participantes?.filter((p) => p.aprovador) ?? [];
  const canApprove =
    obraApprovers.length === 0 || obraApprovers.some((p) => p.nome === getCurrentUserName());

  // Load RDO data
  useEffect(() => {
    const loadRDOData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('Loading RDO with params:', { rdoId, obraId, dateParam });
        
        let rdo: RDO | null = null;
        
        // Priority: rdoId first, then obra + date
        if (rdoId) {
          rdo = await getRDOById(rdoId);
        } else if (obraId && dateParam) {
          rdo = await getRDOByObraDate(obraId, dateParam);
        }

        if (!rdo) {
          setError('RDO não encontrado. Verifique se o RDO existe ou crie um novo relatório.');
          return;
        }
        
        // Add default values for new fields if they don't exist
        if (rdo.approved === undefined) rdo.approved = false;
        if (rdo.approvedBy === undefined) rdo.approvedBy = null;
        if (rdo.approvedAt === undefined) rdo.approvedAt = null;
        if (!rdo.editHistory) rdo.editHistory = [];
        
        // Fase 1: manter o estado da página no formato legado. A conversão para
        // o schema unificado acontece nos adapters por módulo. Manter rdoData e
        // initialDataRef no mesmo formato corrige o "hasUnsavedChanges" falso.
        setRdoData(rdo);
        initialDataRef.current = JSON.parse(JSON.stringify(rdo));
        
        // Start in view mode by default
        setIsEditing(false);
        
      } catch (err) {
        console.error('Error loading RDO:', err);
        setError('Erro ao carregar RDO');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadRDOData();
  }, [rdoId, obraId, dateParam]);
  
  // Check for unsaved changes
  useEffect(() => {
    if (!initialDataRef.current || !rdoData) return;
    const hasChanges = JSON.stringify(rdoData) !== JSON.stringify(initialDataRef.current);
    setHasUnsavedChanges(hasChanges);
  }, [rdoData]);
  
  // Check if section is enabled based on report defaults
  const isSectionEnabled = (sectionKey: keyof NonNullable<typeof reportDefaults>): boolean => {
    if (!reportDefaults) return true; // Fallback: show all sections
    return reportDefaults[sectionKey];
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { variant: "outline" as const, label: "Rascunho" },
      in_review: { variant: "secondary" as const, label: "Em Revisão" },
      signed: { variant: "default" as const, label: "Assinado" },
      approved: { variant: "default" as const, label: "Aprovado" }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Event handlers for editing
  const updateWeatherField = (period: 'morning' | 'afternoon', field: string, value: any) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      weather: {
        ...prev.weather,
        [period]: {
          ...prev.weather?.[period],
          [field]: value
        }
      }
    } : prev);
  };

  const updateWeatherIcon = (period: 'morning' | 'afternoon', icon: WeatherCondition) => {
    updateWeatherField(period, 'icon', icon);
  };

  // Funções para edição e exclusão de mão de obra - matching PreenchendoRDO.tsx
  const handleEditWorkforce = (index: number) => {
    console.log('handleEditWorkforce called with index:', index);
    
    if (!rdoData?.workforce || index >= rdoData.workforce.length) {
      console.warn('Invalid workforce index:', index);
      return;
    }
    
    const workforceItem = rdoData.workforce[index];
    console.log('Editing workforce item:', workforceItem);
    
    setEditingWorkforceIndex(index);
    setEditingWorkforceData(workforceItem);
    
    // Determinar qual modal abrir baseado na estrutura do item
    if ('teamId' in workforceItem && 'teamName' in workforceItem && 'members' in workforceItem) {
      setShowTeamEditModal(true);
    } else {
      setShowIndividualEditModal(true);
    }
  };

  const handleDeleteWorkforce = (index: number) => {
    if (!rdoData?.workforce) return;
    
    const updatedWorkforce = rdoData.workforce.filter((_, i) => i !== index);
    setRdoData(prev => prev ? {
      ...prev,
      workforce: updatedWorkforce
    } : prev);
  };

  const handleAddWorkforce = (workforceData: any) => {
    if (!rdoData) return;
    
    if (editingWorkforceIndex !== null) {
      // Editando item existente
      const updatedWorkforce = [...(rdoData.workforce || [])];
      
      if (workforceData.type === "team") {
        updatedWorkforce[editingWorkforceIndex] = {
          ...workforceData,
          observations: typeof workforceData.observations === 'string' ? workforceData.observations : workforceData.observations || ""
        };
      } else {
        updatedWorkforce[editingWorkforceIndex] = {
          team: workforceData.team,
          function: workforceData.function,
          name: workforceData.name,
          type: workforceData.employeeType,
          assignedTeam: workforceData.assignedTeam,
          quantity: workforceData.quantity,
          observations: typeof workforceData.observations === 'string' ? workforceData.observations : workforceData.observations || ""
        };
      }
      
      setRdoData(prev => prev ? {
        ...prev,
        workforce: updatedWorkforce
      } : prev);
      
      setEditingWorkforceIndex(null);
    } else {
      // Adicionando novo item
      let newWorkforceItem;
      
      if (workforceData.type === "team") {
        newWorkforceItem = {
          ...workforceData,
          observations: typeof workforceData.observations === 'string' ? workforceData.observations : workforceData.observations || ""
        };
      } else {
        newWorkforceItem = {
          team: workforceData.team,
          function: workforceData.function,
          name: workforceData.name,
          type: workforceData.employeeType,
          assignedTeam: workforceData.assignedTeam,
          quantity: workforceData.quantity,
          observations: typeof workforceData.observations === 'string' ? workforceData.observations : workforceData.observations || ""
        };
      }

      setRdoData(prev => prev ? {
        ...prev,
        workforce: [...(prev.workforce || []), newWorkforceItem]
      } : prev);
    }
  };

  const handleAddEquipment = (equipmentData: any) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      equipment: [...(prev.equipment || []), equipmentData]
    } : prev);
  };

  const handleDeleteEquipment = (index: number) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      equipment: (prev.equipment || []).filter((_, i) => i !== index)
    } : prev);
    toast({
      title: "Equipamento removido",
      description: "Equipamento removido com sucesso"
    });
  };

  const handleEditEquipment = (index: number) => {
    setEditingEquipmentIndex(index);
    setShowEditEquipmentModal(true);
  };

  const handleSaveEquipmentEdit = (updatedEquipment: any) => {
    if (editingEquipmentIndex === null || !rdoData?.equipment) return;

    const updatedEquipmentList = [...rdoData.equipment];
    const currentEquipment = updatedEquipmentList[editingEquipmentIndex];
    
    // Update only the fields that exist and are editable
    updatedEquipmentList[editingEquipmentIndex] = {
      ...currentEquipment,
      ...(updatedEquipment.quantity && { quantity: updatedEquipment.quantity }),
      ...(updatedEquipment.status && { ownershipType: updatedEquipment.status }),
      ...(updatedEquipment.leaseExpiryDate && { leaseExpiryDate: updatedEquipment.leaseExpiryDate })
    };

    setRdoData(prev => prev ? {
      ...prev,
      equipment: updatedEquipmentList
    } : prev);

    toast({
      title: "Equipamento atualizado",
      description: "Alterações salvas com sucesso"
    });

    setEditingEquipmentIndex(null);
  };

  const handleDeleteEquipmentFromEdit = () => {
    if (editingEquipmentIndex !== null) {
      handleDeleteEquipment(editingEquipmentIndex);
      setEditingEquipmentIndex(null);
    }
  };

  // Funções específicas para salvar edições dos modais - matching PreenchendoRDO.tsx
  const handleSaveIndividualEdit = (updatedWorkforce: any) => {
    if (editingWorkforceIndex === null || !rdoData) return;
    
    const updatedWorkforceList = [...(rdoData.workforce || [])];
    
    if (updatedWorkforce.quantity === 0) {
      updatedWorkforceList.splice(editingWorkforceIndex, 1);
    } else {
      updatedWorkforceList[editingWorkforceIndex] = updatedWorkforce;
    }
    
    setRdoData(prev => prev ? {
      ...prev,
      workforce: updatedWorkforceList
    } : prev);
    
    setEditingWorkforceIndex(null);
    setEditingWorkforceData(null);
  };

  const handleSaveTeamEdit = (updatedTeam: any) => {
    if (editingWorkforceIndex === null || !rdoData) return;
    
    const updatedWorkforceList = [...(rdoData.workforce || [])];
    
    if (updatedTeam === null || updatedTeam.totalMembers === 0) {
      updatedWorkforceList.splice(editingWorkforceIndex, 1);
    } else {
      updatedWorkforceList[editingWorkforceIndex] = updatedTeam;
    }
    
    setRdoData(prev => prev ? {
      ...prev,
      workforce: updatedWorkforceList
    } : prev);
    
    setEditingWorkforceIndex(null);
    setEditingWorkforceData(null);
  };

  const handleAddMaterial = (materialData: any) => {
    if (!rdoData) return;
    const { type, ...data } = materialData;
    setRdoData(prev => prev ? {
      ...prev,
      materials: {
        ...prev.materials,
        [type]: [...(prev.materials?.[type as keyof typeof prev.materials] || []), data]
      }
    } : prev);
  };

  const handleDeleteMaterial = (type: 'received' | 'used', index: number) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      materials: {
        ...prev.materials,
        [type]: prev.materials?.[type]?.filter((_, i) => i !== index) || []
      }
    } : prev);
  };

  const handleDeleteAttachment = (index: number) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      attachments: prev.attachments?.filter((_, i) => i !== index) || []
    } : prev);
    
    toast({
      title: "Anexo removido",
      description: "Anexo removido com sucesso"
    });
  };


  const handleAddComment = () => {
    if (!newCommentText.trim() || !rdoData) return;
    
    const newComment = {
      id: crypto.randomUUID(),
      user: {
        id: "current-user",
        name: "Usuário Atual",
        role: "Engenheiro"
      },
      datetime: new Date().toISOString(),
      text: newCommentText,
      canEdit: true
    };

    setRdoData(prev => prev ? {
      ...prev,
      comments: [...(prev.comments || []), newComment]
    } : prev);

    setNewCommentText("");
    setShowCommentModal(false);
  };

  const handleAddCommentInternal = (text: string, parentId?: string) => {
    if (!rdoData) return;
    
    const newComment = {
      id: crypto.randomUUID(),
      user: {
        id: "current-user",
        name: "Usuário Atual", 
        role: "Engenheiro"
      },
      datetime: new Date().toISOString(),
      text: text,
      canEdit: true,
      parentId: parentId
    };

    setRdoData(prev => prev ? {
      ...prev,
      comments: [...(prev.comments || []), newComment]
    } : prev);
  };

  const handleEditComment = (commentId: string, newText: string) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      comments: (prev.comments || []).map((comment: any) =>
        comment.id === commentId ? { ...comment, text: newText } : comment
      )
    } : prev);
  };

  const handleDeleteComment = (commentId: string) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      comments: (prev.comments || []).filter((comment: any) => {
        return comment.id !== commentId && comment.parentId !== commentId;
      })
    } : prev);
  };

  const handleDeletePhoto = (photoIndex: number) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      photos: prev.photos?.filter((_, index) => index !== photoIndex) || []
    } : prev);
  };

  const handleDeleteVideo = (videoIndex: number) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      videos: prev.videos?.filter((_, index) => index !== videoIndex) || []
    } : prev);
  };

  const openPhotoViewer = (photoIndex: number) => {
    setCurrentPhotoIndex(photoIndex);
    setPhotoViewerOpen(true);
  };

  const openVideoViewer = (videoIndex: number) => {
    setCurrentVideoIndex(videoIndex);
    setVideoViewerOpen(true);
  };

  const navigatePhoto = React.useCallback((direction: 'prev' | 'next') => {
    try {
      if (!rdoData?.photos || rdoData.photos.length === 0) {
        console.warn('No photos available for navigation');
        return;
      }
      
      if (direction === 'next') {
        setCurrentPhotoIndex((prev) => {
          const nextIndex = (prev + 1) % rdoData.photos!.length;
          return nextIndex;
        });
      } else {
        setCurrentPhotoIndex((prev) => {
          const prevIndex = (prev - 1 + rdoData.photos!.length) % rdoData.photos!.length;
          return prevIndex;
        });
      }
    } catch (error) {
      console.error('Error navigating photo:', error);
      setPhotoViewerOpen(false);
    }
  }, [rdoData?.photos]);

  const navigateVideo = React.useCallback((direction: 'prev' | 'next') => {
    try {
      if (!rdoData?.videos || rdoData.videos.length === 0) {
        console.warn('No videos available for navigation');
        return;
      }
      
      if (direction === 'next') {
        setCurrentVideoIndex((prev) => {
          const nextIndex = (prev + 1) % rdoData.videos!.length;
          return nextIndex;
        });
      } else {
        setCurrentVideoIndex((prev) => {
          const prevIndex = (prev - 1 + rdoData.videos!.length) % rdoData.videos!.length;
          return prevIndex;
        });
      }
    } catch (error) {
      console.error('Error navigating video:', error);
      setVideoViewerOpen(false);
    }
  }, [rdoData?.videos]);

  // Reset video when changing
  useEffect(() => {
    if (videoViewerOpen && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {
        // Auto-play might be blocked by browser policy
      });
    }
  }, [currentVideoIndex, videoViewerOpen]);

  // Handle keyboard navigation with proper cleanup and stable dependencies
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      try {
        if (photoViewerOpen) {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            navigatePhoto('prev');
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            navigatePhoto('next');
          } else if (event.key === 'Escape') {
            event.preventDefault();
            setPhotoViewerOpen(false);
          }
        } else if (videoViewerOpen) {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            navigateVideo('prev');
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            navigateVideo('next');
          } else if (event.key === 'Escape') {
            event.preventDefault();
            setVideoViewerOpen(false);
          }
        }
      } catch (error) {
        console.error('Error handling keyboard event:', error);
      }
    };

    if (photoViewerOpen || videoViewerOpen) {
      window.addEventListener('keydown', handleKeyDown);
      
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [photoViewerOpen, videoViewerOpen, navigatePhoto, navigateVideo]);

  const openCaptionEdit = (index: number, type: 'photo' | 'video', currentCaption: string) => {
    setEditingCaptionIndex(index);
    setEditingCaptionType(type);
    setEditingCaptionText(currentCaption || '');
    setShowCaptionEditModal(true);
  };

  const saveCaptionEdit = () => {
    if (editingCaptionIndex === null || !editingCaptionType || !rdoData) return;

    if (editingCaptionType === 'photo') {
      setRdoData(prev => prev ? {
        ...prev,
        photos: prev.photos?.map((photo, index) => 
          index === editingCaptionIndex 
            ? { ...photo, caption: editingCaptionText }
            : photo
        ) || []
      } : prev);
    } else {
      setRdoData(prev => prev ? {
        ...prev,
        videos: prev.videos?.map((video, index) => 
          index === editingCaptionIndex 
            ? { ...video, title: editingCaptionText }
            : video
        ) || []
      } : prev);
    }

    setShowCaptionEditModal(false);
    setEditingCaptionIndex(null);
    setEditingCaptionType(null);
    setEditingCaptionText('');
  };

  const generateCaptionWithAI = async () => {
    const suggestions = [
      "Registro fotográfico das atividades de construção",
      "Documentação do progresso da obra",
      "Evidência das condições do canteiro",
      "Acompanhamento das etapas executivas",
      "Controle de qualidade dos serviços"
    ];
    
    const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
    setEditingCaptionText(randomSuggestion);
  };
  
  const handleSavePhotos = (photos: any[]) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      photos: [...(prev.photos || []), ...photos]
    } : prev);
  };

  const handleSaveVideos = (videos: any[]) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      videos: [...(prev.videos || []), ...videos]
    } : prev);
  };

  const handleSaveActivityPhotos = (photos: any[]) => {
    if (selectedActivityIndex === null) return;

    // Adicionar a referência da atividade às fotos
    const photosWithActivity = photos.map(photo => ({
      ...photo,
      activityIndex: selectedActivityIndex,
      activityId: `activity_${selectedActivityIndex}`,
      source: 'activity'
    }));

    setRdoData(prev => prev ? {
      ...prev,
      photos: [...(prev.photos || []), ...photosWithActivity]
    } : prev);

    // Reset states
    setSelectedActivityIndex(null);
    setShowActivityPhotoModal(false);

    toast({
      title: "Fotos adicionadas",
      description: `${photos.length} foto${photos.length !== 1 ? 's' : ''} adicionada${photos.length !== 1 ? 's' : ''} à atividade.`
    });
  };

  const handleSaveOccurrencePhotos = (photos: any[]) => {
    if (selectedOccurrenceIndex === null) return;

    // Adicionar a referência da ocorrência às fotos
    const photosWithOccurrence = photos.map(photo => ({
      ...photo,
      occurrenceIndex: selectedOccurrenceIndex,
      occurrenceId: `occurrence_${selectedOccurrenceIndex}`,
      source: 'occurrence'
    }));

    setRdoData(prev => prev ? {
      ...prev,
      photos: [...(prev.photos || []), ...photosWithOccurrence]
    } : prev);

    // Reset states
    setSelectedOccurrenceIndex(null);
    setShowOccurrencePhotoModal(false);

    toast({
      title: "Fotos adicionadas",
      description: `${photos.length} foto${photos.length !== 1 ? 's' : ''} adicionada${photos.length !== 1 ? 's' : ''} à ocorrência.`
    });
  };

  const handleSaveAttachments = (attachments: any[]) => {
    if (!rdoData) return;
    const attachmentData = attachments.map(att => ({
      id: att.id,
      name: att.file.name,
      type: att.file.name.split('.').pop()?.toLowerCase() || 'file',
      size: (att.file.size / (1024 * 1024)).toFixed(2) + ' MB',
      url: URL.createObjectURL(att.file),
      timestamp: new Date().toISOString(),
      uploadedBy: 'Usuário Atual',
      permissions: att.permissions,
      previewable: isPreviewable(att.file.name.split('.').pop()?.toLowerCase() || ''),
      versions: 1
    }));
    
    setRdoData(prev => prev ? {
      ...prev,
      attachments: [...(prev.attachments || []), ...attachmentData]
    } : prev);
  };

  const isPreviewable = (type: string): boolean => {
    const previewableTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp'];
    return previewableTypes.includes(type.toLowerCase());
  };

  const addActivityRow = () => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      activities: [...(prev.activities || []), { 
        activity: '', 
        location: '', 
        completion: 0, 
        responsible: '', 
        description: ''
      }]
    } : prev);
  };
  
  const updateActivityRow = (index: number, field: string, value: any) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      activities: prev.activities?.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ) || []
    } : prev);
  };
  
  const removeActivityRow = (index: number) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      activities: prev.activities?.filter((_, i) => i !== index) || []
    } : prev);
  };

  const addOccurrenceRow = () => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      occurrences: [...(prev.occurrences || []), { 
        type: '', 
        description: '', 
        impact: 'Baixo',
        actionTaken: '',
        responsible: '',
        photos: []
      }]
    } : prev);
  };

  const updateOccurrenceRow = (index: number, field: string, value: any) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      occurrences: prev.occurrences?.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ) || []
    } : prev);
  };

  const removeOccurrenceRow = (index: number) => {
    if (!rdoData) return;
    setRdoData(prev => prev ? {
      ...prev,
      occurrences: prev.occurrences?.filter((_, i) => i !== index) || []
    } : prev);
  };

  // Helper functions for photos
  const getActivityPhotos = (activityIndex: number) => {
    if (!rdoData?.photos) return [];
    return rdoData.photos.filter((photo: any) => 
      photo.activityIndex === activityIndex || photo.activityId === `activity_${activityIndex}`
    );
  };

  const getOccurrencePhotos = (occurrenceIndex: number) => {
    if (!rdoData?.photos) return [];
    return rdoData.photos.filter((photo: any) => 
      photo.occurrenceIndex === occurrenceIndex || photo.occurrenceId === `occurrence_${occurrenceIndex}`
    );
  };

  const getActivityPhotoCount = (activityIndex: number) => {
    return getActivityPhotos(activityIndex).length;
  };

  const getOccurrencePhotoCount = (occurrenceIndex: number) => {
    return getOccurrencePhotos(occurrenceIndex).length;
  };

  const handleOpenActivityGallery = (activityIndex: number) => {
    const photos = getActivityPhotos(activityIndex);
    if (photos.length > 0) {
      setCurrentPhotoIndex(0);
      setPhotoViewerOpen(true);
    }
  };

  const handleOpenOccurrenceGallery = (occurrenceIndex: number) => {
    const photos = getOccurrencePhotos(occurrenceIndex);
    if (photos.length > 0) {
      setCurrentPhotoIndex(0);
      setPhotoViewerOpen(true);
    }
  };
  
  // Action handlers
  const handleSaveChanges = async () => {
    if (!rdoData) return;
    
    try {
      // Add edit history entry
      const currentUser = getCurrentUserName();
      const editEntry = {
        user: currentUser,
        at: new Date().toISOString()
      };
      
      const updatedRDO = {
        ...rdoData,
        editHistory: [...(rdoData.editHistory || []), editEntry]
      };
      
      const result = await updateRDO(rdoData.id, updatedRDO);
      
      if (result) {
        setRdoData(result);
        initialDataRef.current = JSON.parse(JSON.stringify(result));
        setHasUnsavedChanges(false);
        
        toast({
          title: "Alterações salvas",
          description: "RDO atualizado com sucesso."
        });
      } else {
        throw new Error('Falha ao atualizar RDO');
      }
    } catch (err) {
      console.error('Error saving RDO:', err);
      toast({
        title: "Erro",
        description: "Erro ao salvar alterações.",
        variant: "destructive"
      });
    }
  };

  const handleCancelEditing = () => {
    if (hasUnsavedChanges) {
      setConfirmCancelOpen(true);
    } else {
      setIsEditing(false);
    }
  };
  
  const confirmCancelEditing = () => {
    if (!initialDataRef.current) return;
    setRdoData(JSON.parse(JSON.stringify(initialDataRef.current)));
    setIsEditing(false);
    setHasUnsavedChanges(false);
    setConfirmCancelOpen(false);
  };

  const handleExport = (type: 'pdf' | 'whatsapp' | 'email' | 'link') => {
    setPendingExportType(type);
    setIsExportOptionsModalOpen(true);
  };

  const handleExportConfirm = async (selectedSections: string[], recipients?: string[]) => {
    const publicLink = rdoData?.id
      ? `${window.location.origin}/rdo-public/${rdoData.id}`
      : window.location.href;
    const baseText = `RDO ${obra?.nomeObra ?? ""} - ${rdoData?.date ?? ""}`.trim();

    switch (pendingExportType) {
      case "pdf":
        await pdfExportService.exportPDFWithSpinner({
          selectedSections,
          obraId: obraId ?? undefined,
          rdoData,
        });
        break;
      case "link":
        try {
          await navigator.clipboard.writeText(publicLink);
          toast({ title: "Link gerado", description: "Link do RDO copiado para a área de transferência." });
        } catch {
          toast({ title: "Link do RDO", description: publicLink });
        }
        break;
      case "whatsapp":
        openWhatsApp({ text: `${baseText}\n${publicLink}` });
        break;
      case "email": {
        const to = (recipients ?? []).join(",");
        const subject = encodeURIComponent(baseText || "Relatório Diário de Obra");
        const body = encodeURIComponent(`Segue o relatório diário de obra:\n${publicLink}`);
        window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
        break;
      }
    }
  };

  const handleSign = (signatureData: any) => {
    console.log('Signing RDO:', signatureData);
  };

  const handleApprove = () => {
    console.log('Approving RDO');
  };

  const handleSignAndApprove = () => {
    setIsSignatureModalOpen(true);
  };

  const handleSignatureProcess = async (signatureData: string) => {
    // Fase 4: persistir a aprovação de verdade (antes era apenas console.log).
    if (!rdoData?.id) {
      toast({
        title: "Erro",
        description: "RDO sem identificador; não é possível aprovar.",
        variant: "destructive",
      });
      return;
    }

    try {
      const approvedBy = getCurrentUserName();
      const approvedAt = new Date().toISOString();
      const result = await updateRDO(rdoData.id, {
        approved: true,
        approvedBy,
        approvedAt,
        status: "approved",
      });

      if (!result) throw new Error("Falha ao aprovar RDO");

      setRdoData(result);
      initialDataRef.current = JSON.parse(JSON.stringify(result));
      setIsEditing(false);
      setHasUnsavedChanges(false);
      setIsSignatureModalOpen(false);

      toast({
        title: "RDO aprovado",
        description: `Relatório aprovado por ${approvedBy}.`,
      });
    } catch (err) {
      console.error("Error approving RDO:", err);
      toast({
        title: "Erro",
        description: "Não foi possível aprovar o RDO.",
        variant: "destructive",
      });
    }
  };

  const handlePreviousRDO = () => {
    if (!prevSibling) {
      toast({ title: "RDO anterior", description: "Não há RDO anterior para esta obra." });
      return;
    }
    navigate(`/rdo?obra=${obraId}&rdoId=${prevSibling.id}`);
  };

  const handleNextRDO = () => {
    if (!nextSibling) {
      toast({ title: "Próximo RDO", description: "Não há RDO posterior para esta obra." });
      return;
    }
    navigate(`/rdo?obra=${obraId}&rdoId=${nextSibling.id}`);
  };

  // Get last edit info for display
  const getLastEditInfo = () => {
    if (!rdoData?.editHistory || rdoData.editHistory.length === 0) return null;
    
    const lastEdit = rdoData.editHistory[rdoData.editHistory.length - 1];
    const date = new Date(lastEdit.at);
    
    return {
      user: lastEdit.user,
      date: date.toLocaleDateString('pt-BR'),
      time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Render loading state
  if (isLoading || isLoadingDefaults) {
    return (
      <div className="min-h-screen bg-background px-2 py-2">
        <div className="max-w-5xl mx-auto space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="min-h-screen bg-background p-4 sm:p-6">
        <div className="mx-auto max-w-6xl">
          <Alert className="mb-6">
            <AlertDescription>
              {error}
            </AlertDescription>
          </Alert>

          {/* As três ações não cabem lado a lado em 390px — daí o overflow
              horizontal da página inteira. Quebram em linha no mobile. */}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex h-11 items-center gap-2 sm:h-10"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>

            <Button
              onClick={() => window.location.reload()}
              className="flex h-11 items-center gap-2 sm:h-10"
            >
              <Calendar className="h-4 w-4" />
              Tentar Novamente
            </Button>

            <Button
              onClick={() => navigate('/obras')}
              className="flex h-11 items-center gap-2 sm:h-10"
            >
              <Building className="h-4 w-4" />
              Ver Obras
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!rdoData) {
    return (
      <div className="min-h-screen bg-background px-2 py-2">
        <div className="max-w-5xl mx-auto">
          <Alert className="mb-4">
            <AlertDescription>RDO não encontrado.</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Parse no fuso local — evita o off-by-one de "YYYY-MM-DD" interpretado como UTC
  const currentDate = parseYmdLocal(rdoData.date);
  const lastEditInfo = getLastEditInfo();

  return (
    <>
      <PrintStyles />
      
      <div className="min-h-screen bg-background px-2 py-2">
        <div className="max-w-5xl mx-auto space-y-1.5">
          
          {/* Error state */}
          {defaultsError && (
            <Alert className="mb-4">
              <AlertDescription>
                {defaultsError}. Exibindo todas as seções como fallback.
              </AlertDescription>
            </Alert>
          )}

          {/* Edit mode indicator */}
          {isEditing && !rdoData.approved && (
            <Alert className="mb-4">
              <Edit className="h-4 w-4" />
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <span>Modo de edição ativado. {hasUnsavedChanges ? 'Você tem alterações não salvas.' : ''}</span>
                  <Badge variant="secondary">Editando</Badge>
                </div>
              </AlertDescription>
            </Alert>
          )}
        
          {/* Header Principal - Always shown */}
          <Card data-section="header">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <h1 className="text-lg font-bold">RDO — {format(currentDate, "dd/MM/yyyy", { locale: ptBR })}</h1>
                  <p className="text-sm text-muted-foreground">Obra: {obra?.nomeObra || obraId}</p>
                  {lastEditInfo && (
                    <p className="text-xs text-muted-foreground">
                      Última edição por {lastEditInfo.user} em {lastEditInfo.date} às {lastEditInfo.time}
                    </p>
                  )}
                </div>
                <div className="text-right space-y-1">
                  <p className="text-sm font-medium">{format(currentDate, "dd/MM/yyyy", { locale: ptBR })}</p>
                  {getStatusBadge(rdoData.status || 'draft')}
                  {rdoData.approved && (
                    <Badge variant="default" className="ml-2">Aprovado</Badge>
                  )}
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Condições Climáticas */}
          {isSectionEnabled('condicoesClimaticas') && (
            <Card data-section="weather">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Cloud className="w-5 h-5" />
                  Condições Climáticas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Morning Weather */}
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                      <Sun className="w-4 h-4 text-yellow-500" />
                      Manhã
                    </h4>
                    {isEditing && !rdoData.approved ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <WeatherIconButton
                            value={rdoData.weather?.morning?.icon}
                            onChange={(icon) => updateWeatherIcon('morning', icon)}
                          />
                          <div className="flex items-center gap-2">
                            <Label htmlFor="morning-workable">Praticável:</Label>
                            <Switch
                              id="morning-workable"
                              checked={rdoData.weather?.morning?.workable || false}
                              onCheckedChange={(checked) => updateWeatherField('morning', 'workable', checked)}
                            />
                          </div>
                        </div>
                        <Textarea
                          placeholder="Observações da manhã..."
                          value={rdoData.weather?.morning?.observation || ''}
                          onChange={(e) => updateWeatherField('morning', 'observation', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Tempo:</span> {rdoData.weather?.morning?.condition || 'Não informado'}</div>
                        <div>
                          <span className="font-medium">Condição:</span>
                          {/* Ausência de registro != impraticável — não inventar o dado */}
                          <Badge
                            variant={
                              rdoData.weather?.morning?.workable === undefined
                                ? "secondary"
                                : rdoData.weather.morning.workable ? "default" : "destructive"
                            }
                            className="ml-2"
                          >
                            {rdoData.weather?.morning?.workable === undefined
                              ? "Não informado"
                              : rdoData.weather.morning.workable ? "Praticável" : "Impraticável"}
                          </Badge>
                        </div>
                        {rdoData.weather?.morning?.observation && (
                          <div><span className="font-medium">Observação:</span> {rdoData.weather.morning.observation}</div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Afternoon Weather */}
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                      <Cloud className="w-4 h-4 text-blue-500" />
                      Tarde
                    </h4>
                    {isEditing && !rdoData.approved ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <WeatherIconButton
                            value={rdoData.weather?.afternoon?.icon}
                            onChange={(icon) => updateWeatherIcon('afternoon', icon)}
                          />
                          <div className="flex items-center gap-2">
                            <Label htmlFor="afternoon-workable">Praticável:</Label>
                            <Switch
                              id="afternoon-workable"
                              checked={rdoData.weather?.afternoon?.workable || false}
                              onCheckedChange={(checked) => updateWeatherField('afternoon', 'workable', checked)}
                            />
                          </div>
                        </div>
                        <Textarea
                          placeholder="Observações da tarde..."
                          value={rdoData.weather?.afternoon?.observation || ''}
                          onChange={(e) => updateWeatherField('afternoon', 'observation', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Tempo:</span> {rdoData.weather?.afternoon?.condition || 'Não informado'}</div>
                        <div>
                          <span className="font-medium">Condição:</span>
                          {/* Ausência de registro != impraticável — não inventar o dado */}
                          <Badge
                            variant={
                              rdoData.weather?.afternoon?.workable === undefined
                                ? "secondary"
                                : rdoData.weather.afternoon.workable ? "default" : "destructive"
                            }
                            className="ml-2"
                          >
                            {rdoData.weather?.afternoon?.workable === undefined
                              ? "Não informado"
                              : rdoData.weather.afternoon.workable ? "Praticável" : "Impraticável"}
                          </Badge>
                        </div>
                        {rdoData.weather?.afternoon?.observation && (
                          <div><span className="font-medium">Observação:</span> {rdoData.weather.afternoon.observation}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mão de Obra */}
          {getFlag("useNewWorkforceInRDO") ? (
            <WorkforceForRDO
              legacyValue={rdoData.workforce || []}
              onLegacyChange={(updatedWorkforce) => {
                setRdoData(prev => ({ ...prev, workforce: updatedWorkforce }));
              }}
              mode={isEditing && !rdoData.approved ? "edit" : "view"}
              // Legacy props for fallback
              rdoData={rdoData}
              workforceViewMode={workforceViewMode}
              setWorkforceViewMode={setWorkforceViewMode}
              showWorkforceModal={showWorkforceModal}
              setShowWorkforceModal={setShowWorkforceModal}
              handleAddWorkforce={handleAddWorkforce}
              handleEditWorkforce={handleEditWorkforce}
              handleDeleteWorkforce={handleDeleteWorkforce}
              isEditing={isEditing}
              isSectionEnabled={isSectionEnabled}
            />
          ) : (
            <LegacyWorkforceSection
              rdoData={rdoData}
              workforceViewMode={workforceViewMode}
              setWorkforceViewMode={setWorkforceViewMode}
              showWorkforceModal={showWorkforceModal}
              setShowWorkforceModal={setShowWorkforceModal}
              handleAddWorkforce={handleAddWorkforce}
              handleEditWorkforce={handleEditWorkforce}
              handleDeleteWorkforce={handleDeleteWorkforce}
              isEditing={isEditing}
              isSectionEnabled={isSectionEnabled}
            />
          )}

          {/* Equipamentos
              Adapter auto-gated: o próprio EquipmentsForRDO decide entre o
              módulo novo e a seção legada via getFlag("useNewEquipmentsInRDO"),
              encaminhando os props legados abaixo. Por isso não há ternário de
              getFlag no call-site (diferente dos demais módulos). */}
          <EquipmentsForRDO
            legacyValue={rdoData.equipment || []}
            onLegacyChange={(equipment) => {
              setRdoData(prev => ({ ...prev, equipment }));
            }}
            mode={isEditing && !rdoData.approved ? "edit" : "view"}
            // Legacy props for fallback
            showEquipmentModal={showEquipmentModal}
            setShowEquipmentModal={setShowEquipmentModal}
            showEditEquipmentModal={showEditEquipmentModal}
            setShowEditEquipmentModal={setShowEditEquipmentModal}
            editingEquipment={editingEquipmentIndex}
            setEditingEquipment={setEditingEquipmentIndex}
            handleAddEquipment={handleAddEquipment}
            handleEditEquipment={handleEditEquipment}
            handleDeleteEquipment={handleDeleteEquipment}
            isEditing={isEditing}
            isSectionEnabled={isSectionEnabled}
          />

          {/* Atividades */}
          {isSectionEnabled('atividades') && (
            getFlag("useNewActivitiesInRDO") ? (
              <ActivitiesForRDO
                legacyValue={rdoData.activities || []}
                onLegacyChange={(updatedActivities) => {
                  setRdoData(prev => ({ ...prev, activities: updatedActivities }));
                }}
                mode={isEditing && !rdoData.approved ? "edit" : "view"}
              />
            ) : (
              <LegacyActivitiesSection
                value={rdoData.activities || []}
                onChange={(updatedActivities) => {
                  setRdoData(prev => ({ ...prev, activities: updatedActivities }));
                }}
                mode={isEditing && !rdoData.approved ? "edit" : "view"}
              />
            )
          )}

          {/* Ocorrências */}
          {isSectionEnabled('ocorrencias') && (
            getFlag("useNewOccurrencesInRDO") ? (
              <OccurrencesForRDO
                legacyValue={rdoData.occurrences || []}
                onLegacyChange={(occurrences) => setRdoData(prev => ({ ...prev, occurrences }))}
                mode={isEditing && !rdoData.approved ? "edit" : "view"}
                onMirrorPhotos={(mirrored) =>
                  setRdoData(prev => ({ ...prev, photos: [...(prev.photos || []), ...mirrored] }))
                }
              />
            ) : (
            <Card data-section="occurrences">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Ocorrências ({rdoData.occurrences?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing && !rdoData.approved ? (
                  <div className="space-y-4">
                    {rdoData.occurrences?.map((occurrence, index) => (
                      <div key={index} className="border rounded-lg p-2 space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-sm">Ocorrência {index + 1}</h4>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const fileInput = document.createElement('input');
                                fileInput.type = 'file';
                                fileInput.accept = 'image/jpeg,image/jpg,image/png,image/heic';
                                fileInput.multiple = true;
                                fileInput.onchange = (e) => {
                                  const files = (e.target as HTMLInputElement).files;
                                  if (files && files.length > 0) {
                                    setSelectedOccurrenceIndex(index);
                                    setSelectedOccurrenceFiles(files);
                                    setShowOccurrencePhotoModal(true);
                                  }
                                };
                                fileInput.click();
                              }}
                              aria-label="Enviar fotos da ocorrência"
                              className="h-8 w-8 p-0"
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeOccurrenceRow(index)}
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor={`occurrence-type-${index}`}>Tipo</Label>
                            <Input
                              id={`occurrence-type-${index}`}
                              value={occurrence.type}
                              onChange={(e) => updateOccurrenceRow(index, 'type', e.target.value)}
                              placeholder="Ex: Segurança, Qualidade, Prazo"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`occurrence-impact-${index}`}>Impacto</Label>
                            <Select
                              value={occurrence.impact}
                              onValueChange={(value) => updateOccurrenceRow(index, 'impact', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Baixo">Baixo</SelectItem>
                                <SelectItem value="Médio">Médio</SelectItem>
                                <SelectItem value="Alto">Alto</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="md:col-span-2 grid md:grid-cols-2 gap-4 items-start">
                            <div>
                              <Label htmlFor={`occurrence-responsible-${index}`}>Responsável</Label>
                              <Input
                                id={`occurrence-responsible-${index}`}
                                value={occurrence.responsible}
                                onChange={(e) => updateOccurrenceRow(index, 'responsible', e.target.value)}
                                placeholder="Ex: Técnico de Segurança"
                              />
                            </div>
                            
                            {/* Photo thumbnails */}
                            <div className="flex items-end gap-2 flex-wrap">
                              {(() => {
                                const occurrencePhotos = getOccurrencePhotos(index);
                                const displayPhotos = occurrencePhotos.slice(0, 2);
                                const remainingCount = Math.max(0, occurrencePhotos.length - 2);
                                
                                return (
                                  <>
                                    {displayPhotos.map((photo: any, photoIndex: number) => (
                                      <div
                                        key={photoIndex}
                                        className="w-16 h-16 border rounded cursor-pointer hover:scale-105 transition-transform overflow-hidden bg-muted"
                                        onClick={() => handleOpenOccurrenceGallery(index)}
                                        title={photo.caption || 'Ver foto'}
                                      >
                                        {photo.url ? (
                                          <img 
                                            src={photo.url} 
                                            alt={photo.caption} 
                                            className="w-full h-full object-cover" 
                                          />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center">
                                            <Image className="w-4 h-4 text-muted-foreground" />
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    
                                    {remainingCount > 0 && (
                                      <div
                                        className="w-16 h-16 border rounded cursor-pointer hover:scale-105 transition-transform bg-muted flex items-center justify-center text-sm font-medium"
                                        onClick={() => handleOpenOccurrenceGallery(index)}
                                        title={`Ver mais ${remainingCount} foto${remainingCount !== 1 ? 's' : ''}`}
                                      >
                                        +{remainingCount}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor={`occurrence-description-${index}`}>Descrição</Label>
                          <Textarea
                            id={`occurrence-description-${index}`}
                            value={occurrence.description}
                            onChange={(e) => updateOccurrenceRow(index, 'description', e.target.value)}
                            placeholder="Descreva detalhadamente a ocorrência"
                            rows={3}
                          />
                        </div>
                        <div>
                          <Label htmlFor={`occurrence-action-${index}`}>Ação Tomada</Label>
                          <Textarea
                            id={`occurrence-action-${index}`}
                            value={occurrence.actionTaken}
                            onChange={(e) => updateOccurrenceRow(index, 'actionTaken', e.target.value)}
                            placeholder="Descreva as ações tomadas para resolver a ocorrência"
                            rows={2}
                          />
                        </div>
                        
                         {/* Hide Photos Checkbox */}
                         {getOccurrencePhotoCount(index) > 0 && (
                           <div className="flex items-center space-x-2">
                             <Switch
                               checked={occurrence.hidePhotos || false}
                               onCheckedChange={(checked) => updateOccurrenceRow(index, 'hidePhotos', checked)}
                             />
                             <Label className="text-sm">Ocultar fotos do container principal</Label>
                           </div>
                         )}
                      </div>
                    ))}
                    {(!rdoData.occurrences || rdoData.occurrences.length === 0) && (
                      <div className="text-center text-muted-foreground py-8">
                        Nenhuma ocorrência registrada
                      </div>
                    )}
                    
                    <Button
                      variant="outline"
                      onClick={addOccurrenceRow}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Ocorrência
                    </Button>
                  </div>
                ) : (
                  <>
                    {rdoData.occurrences && rdoData.occurrences.length > 0 ? (
                      <div className="space-y-4">
                        {rdoData.occurrences.map((occurrence, index) => (
                          <div key={index} className="border rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline">{occurrence.type}</Badge>
                              <Badge variant={occurrence.impact === "Alto" ? "destructive" : occurrence.impact === "Médio" ? "secondary" : "default"}>
                                Impacto {occurrence.impact}
                              </Badge>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div><span className="font-medium">Descrição:</span> {occurrence.description}</div>
                              <div><span className="font-medium">Ação Tomada:</span> {occurrence.actionTaken}</div>
                              <div><span className="font-medium">Responsável:</span> {occurrence.responsible}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">Nenhuma ocorrência registrada.</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
            )
          )}

          {/* Materiais */}
          {isSectionEnabled('materiais') && (
            <Card data-section="materials">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Materiais
                </CardTitle>
              </CardHeader>
              <CardContent>
                {getFlag("useNewMaterialsInRDO") ? (
                  <MaterialsForRDO
                    legacyValue={rdoData.materials || { received: [], used: [] }}
                    onLegacyChange={(materials) => setRdoData(prev => ({ ...prev, materials }))}
                    mode={rdoData.approved ? "view" : "edit"}
                  />
                ) : (
                  <LegacyMaterialsSection 
                    materialsData={rdoData.materials as any || { received: [], used: [] }}
                    onAdd={(material) => {
                      // Handle add material logic for legacy
                      console.log('Add material (legacy):', material);
                    }}
                    isEditable={!rdoData.approved}
                  />
                )}
              </CardContent>
            </Card>
          )}

          {/* Comentários */}
          {isSectionEnabled('comentarios') && (
            getFlag("useNewCommentsInRDO") ? (
              <CommentsForRDO
                legacyValue={rdoData.comments || []}
                onLegacyChange={(comments) => setRdoData(prev => ({ ...prev, comments }))}
                mode={rdoData.approved ? "view" : "edit"}
                currentUserId="current-user"
                disabled={false}
              />
            ) : (
              <LegacyCommentsSection
                value={rdoData.comments || []}
                onChange={(comments) => setRdoData(prev => ({ ...prev, comments }))}
                mode={rdoData.approved ? "view" : "edit"}
              />
            )
          )}

          {/* Fotos */}
          {isSectionEnabled('fotos') && (
            getFlag("useNewPhotosInRDO") ? (
              <PhotosForRDO
                legacyValue={rdoData.photos || []}
                onLegacyChange={(photos) => setRdoData(prev => ({ ...prev, photos }))}
                mode={rdoData.approved ? "view" : "edit"}
              />
            ) : (
              <LegacyPhotosSection
                value={rdoData.photos || []}
                onChange={(photos) => setRdoData(prev => ({ ...prev, photos }))}
                mode={rdoData.approved ? "view" : "edit"}
              />
            )
          )}

          {/* Vídeos */}
          {isSectionEnabled('videos') && (
            getFlag("useNewVideosInRDO") ? (
              <VideosForRDO
                legacyValue={rdoData.videos || []}
                onLegacyChange={(videos) => setRdoData(prev => ({ ...prev, videos }))}
                mode={rdoData.approved ? "view" : "edit"}
              />
            ) : (
              <LegacyVideosSection
                value={rdoData.videos || []}
                onChange={(videos) => setRdoData(prev => ({ ...prev, videos }))}
                mode={rdoData.approved ? "view" : "edit"}
              />
            )
          )}

          {/* Anexos */}
          {getFlag("useNewAttachmentsInRDO") ? (
            <AttachmentsForRDO
              legacyValue={rdoData.attachments || []}
              onLegacyChange={(attachments) => {
                setRdoData(prev => ({ ...prev, attachments }));
              }}
              mode={isEditing && !rdoData.approved ? "edit" : "view"}
              // Legacy props for fallback
              showAttachmentModal={showAttachmentModal}
              setShowAttachmentModal={setShowAttachmentModal}
              handleDeleteAttachment={handleDeleteAttachment}
              isEditing={isEditing}
              isSectionEnabled={isSectionEnabled}
            />
          ) : (
            <LegacyAttachmentsSection
              value={rdoData.attachments || []}
              onChange={(attachments) => setRdoData(prev => ({ ...prev, attachments }))}
              mode={isEditing && !rdoData.approved ? "edit" : "view"}
              showAttachmentModal={showAttachmentModal}
              setShowAttachmentModal={setShowAttachmentModal}
              handleDeleteAttachment={handleDeleteAttachment}
              isEditing={isEditing}
              isSectionEnabled={isSectionEnabled}
            />
          )}

          {/* Assinaturas */}
          <Card data-section="signatures">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSignature className="w-5 h-5" />
                Assinaturas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Assinatura do responsável — reflete a aprovação real do RDO */}
                <SignatureField
                  title="Responsável pela obra"
                  userName={rdoData.approvedBy || getCurrentUserName()}
                  isSigned={!!rdoData.approved}
                  signatureDate={
                    rdoData.approvedAt
                      ? new Date(rdoData.approvedAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
                      : undefined
                  }
                  onSign={() => setIsSignatureModalOpen(true)}
                />

                {/* Assinatura do Cliente — fluxo de assinatura do cliente ainda não implementado */}
                <SignatureField
                  title={obra?.clienteObra ? getClienteNomeById(obra.clienteObra) : "Cliente"}
                  userName={obra?.clienteObra ? getClienteNomeById(obra.clienteObra) : "—"}
                  isSigned={false}
                  onSign={() => setIsSignatureModalOpen(true)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Footer com Botões de Ação */}
          <div className="sticky bottom-0 bg-background border-t border-border p-4 print:hidden">
            <div className="flex flex-wrap items-center justify-between gap-4">
              
              {/* Navegação - Apenas RDO Anterior */}
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handlePreviousRDO} disabled={!prevSibling} className="h-8 px-2 py-1 text-xs">
                  <span className="flex flex-col items-center">
                    <span className="text-xs opacity-70">{prevSibling ? formatYmd(prevSibling.date) : '—'}</span>
                    <span className="flex items-center">
                      <ChevronLeft className="w-3 h-3 mr-1" />
                      RDO Anterior
                    </span>
                  </span>
                </Button>
              </div>

              {/* Ações centrais */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  {isEditing && !rdoData.approved ? (
                    <>
                      <Button 
                        onClick={handleSaveChanges}
                        disabled={!hasUnsavedChanges}
                        className="gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Salvar alterações
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={handleCancelEditing}
                      >
                        Cancelar
                      </Button>
                    </>
                  ) : rdoData.approved ? (
                    <Badge variant="default" className="px-3 py-2">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      RDO Aprovado
                    </Badge>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                  )}
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Exportar
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => handleExport('pdf')}>
                        <FileText className="w-4 h-4 mr-2" />
                        Exportar PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('email')}>
                        <Mail className="w-4 h-4 mr-2" />
                        Enviar por Email
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('whatsapp')}>
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Enviar WhatsApp
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('link')}>
                        <Link2 className="w-4 h-4 mr-2" />
                        Gerar Link
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {!rdoData.approved && canApprove && (
                  <Button variant="outline" size="sm" onClick={handleSignAndApprove}>
                    <FileSignature className="w-4 h-4 mr-2" />
                    Assinar e aprovar RDO
                  </Button>
                )}
              </div>

              {/* Próximo RDO - Extrema direita */}
              <div className="flex flex-col items-end">
                <Button variant="ghost" size="sm" onClick={handleNextRDO} disabled={!nextSibling} className="h-8 px-2 py-1 text-xs">
                  <span className="flex flex-col items-center">
                    <span className="text-xs opacity-70">{nextSibling ? formatYmd(nextSibling.date) : '—'}</span>
                    <span className="flex items-center">
                      Próximo RDO
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </span>
                  </span>
                </Button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Modals */}
      <SignaturePopup
        open={isSignatureModalOpen}
        onOpenChange={setIsSignatureModalOpen}
        onSignAndApprove={handleSignatureProcess}
        userName={getCurrentUserName()}
        userRole={getCurrentUserEmail() ?? ""}
        hasApprovalRule={true}
      />

      <PreencherRelatorioModal
        open={isNewRDOModalOpen}
        onOpenChange={setIsNewRDOModalOpen}
        defaultObra={obraId || undefined}
      />

      <ExportOptionsModal
        open={isExportOptionsModalOpen}
        onOpenChange={setIsExportOptionsModalOpen}
        exportType={pendingExportType}
        onExport={handleExportConfirm}
      />

      {/* Equipment Edit Modal */}
      {editingEquipmentIndex !== null && rdoData?.equipment && (
        <EditEquipmentModal
          open={showEditEquipmentModal}
          onOpenChange={setShowEditEquipmentModal}
          equipment={{
            tipo: rdoData.equipment[editingEquipmentIndex].type || '',
            identificacao: rdoData.equipment[editingEquipmentIndex].identification || '',
            condicao: rdoData.equipment[editingEquipmentIndex].condition || '',
            operador: rdoData.equipment[editingEquipmentIndex].operator || '',
            tempoOcioso: rdoData.equipment[editingEquipmentIndex].idleTime?.toString() || '',
            status: (rdoData.equipment[editingEquipmentIndex] as any).ownershipType === 'locado' ? 'locado' : 'proprio',
            quantity: (rdoData.equipment[editingEquipmentIndex] as any).quantity || 1,
            leaseExpiryDate: (rdoData.equipment[editingEquipmentIndex] as any).leaseExpiryDate
          }}
          onSave={handleSaveEquipmentEdit}
          onDelete={handleDeleteEquipmentFromEdit}
        />
      )}

      {/* Workforce Individual Edit Modal */}
      {editingWorkforceIndex !== null && editingWorkforceData && showIndividualEditModal && (
        <WorkforceIndividualEditModal
          open={showIndividualEditModal}
          onOpenChange={setShowIndividualEditModal}
          workforce={editingWorkforceData}
          onSave={handleSaveIndividualEdit}
        />
      )}

      {/* Workforce Team Edit Modal */}
      {editingWorkforceIndex !== null && editingWorkforceData && showTeamEditModal && (
        <WorkforceTeamEditModal
          open={showTeamEditModal}
          onOpenChange={setShowTeamEditModal}
          teamData={editingWorkforceData}
          onSave={handleSaveTeamEdit}
        />
      )}

      {/* Photo Viewer Modal */}
      <Dialog open={photoViewerOpen} onOpenChange={setPhotoViewerOpen}>
        <DialogContent className="max-w-4xl w-[90vw] h-[90vh] p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Visualizador de Fotos</DialogTitle>
            <DialogDescription>
              Visualize as fotos do relatório com navegação por teclado.
            </DialogDescription>
          </DialogHeader>
          <div className="relative h-full flex flex-col">
            {rdoData?.photos && rdoData.photos.length > 0 && (
              <>
                {/* Photo Display Area */}
                <div className="flex-1 relative bg-black rounded-t-lg overflow-hidden">
                  <img
                    src={rdoData.photos[currentPhotoIndex]?.url}
                    alt={rdoData.photos[currentPhotoIndex]?.caption}
                    className="w-full h-full object-contain"
                  />
                  
                  {/* Navigation Buttons */}
                  {rdoData.photos.length > 1 && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                        onClick={() => navigatePhoto('prev')}
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                        onClick={() => navigatePhoto('next')}
                      >
                        <ChevronRight className="w-6 h-6" />
                      </Button>
                    </>
                  )}
                  
                  {/* Close Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white"
                    onClick={() => setPhotoViewerOpen(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  
                  {/* Photo Counter */}
                  {rdoData.photos.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                      {currentPhotoIndex + 1} / {rdoData.photos.length}
                    </div>
                  )}
                </div>
                
                {/* Caption Area */}
                {rdoData.photos[currentPhotoIndex]?.caption && (
                  <div className="p-4 bg-background border-t">
                    <p className="text-sm text-center text-muted-foreground">
                      {rdoData.photos[currentPhotoIndex].caption}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Viewer Modal */}
      <Dialog open={videoViewerOpen} onOpenChange={setVideoViewerOpen}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Visualizador de Vídeos</DialogTitle>
            <DialogDescription>
              Visualize os vídeos do relatório com navegação por teclado.
            </DialogDescription>
          </DialogHeader>
          <div className="relative h-full flex flex-col bg-black rounded-lg overflow-hidden">
            {rdoData?.videos && rdoData.videos.length > 0 && (
              <>
                {/* Video Display Area */}
                <div className="flex-1 relative flex items-center justify-center p-4">
                  <div className="w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden relative">
                  <video
                    ref={videoRef}
                    key={currentVideoIndex} // Force re-render when video changes
                    className="w-full h-full object-contain"
                    controls
                    muted
                    autoPlay
                    preload="metadata"
                    onError={(e) => {
                      console.error('Video loading error:', e);
                      // Handle video error gracefully
                    }}
                    onLoadStart={() => {
                      console.log('Video loading started');
                    }}
                  >
                    {rdoData.videos?.[currentVideoIndex]?.url && (
                      <source src={rdoData.videos[currentVideoIndex].url} type="video/mp4" />
                    )}
                    Seu navegador não suporta reprodução de vídeo.
                  </video>
                    
                    {/* Navigation Arrows */}
                    {rdoData.videos.length > 1 && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white border-0 transition-all duration-200 hover:scale-110"
                          onClick={() => navigateVideo('prev')}
                        >
                          <ChevronLeft className="w-8 h-8" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white border-0 transition-all duration-200 hover:scale-110"
                          onClick={() => navigateVideo('next')}
                        >
                          <ChevronRight className="w-8 h-8" />
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {/* Close Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white border-0"
                    onClick={() => setVideoViewerOpen(false)}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                  
                  {/* Video Counter */}
                  {rdoData.videos.length > 1 && (
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
                      {currentVideoIndex + 1} / {rdoData.videos.length}
                    </div>
                  )}
                </div>
                
                {/* Video Info */}
                <div className="bg-background border-t p-4 space-y-2">
                  <div className="text-center">
                    <h3 className="font-medium text-lg">
                      {rdoData.videos[currentVideoIndex]?.title || `Vídeo ${currentVideoIndex + 1}`}
                    </h3>
                    {rdoData.videos[currentVideoIndex]?.duration && (
                      <p className="text-sm text-muted-foreground">
                        Duração: {rdoData.videos[currentVideoIndex].duration}
                      </p>
                    )}
                  </div>
                  
                  {/* Video Thumbnails Navigation */}
                  {rdoData.videos.length > 1 && (
                    <div className="flex justify-center gap-2 mt-4 overflow-x-auto py-2">
                      {rdoData.videos.map((video: any, index: number) => (
                        <button
                          key={index}
                          onClick={() => setCurrentVideoIndex(index)}
                          className={`flex-shrink-0 w-20 h-12 bg-muted rounded border-2 transition-all duration-200 hover:scale-105 ${
                            index === currentVideoIndex 
                              ? 'border-primary shadow-lg' 
                              : 'border-transparent hover:border-primary/50'
                          }`}
                        >
                          <div className="w-full h-full flex items-center justify-center">
                            <FileVideo className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Caption Edit Modal */}
      <Dialog open={showCaptionEditModal} onOpenChange={setShowCaptionEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Editar {editingCaptionType === 'photo' ? 'Legenda da Foto' : 'Título do Vídeo'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="caption-text">
                {editingCaptionType === 'photo' ? 'Legenda' : 'Título'}
              </Label>
              <Textarea
                id="caption-text"
                value={editingCaptionText}
                onChange={(e) => setEditingCaptionText(e.target.value)}
                placeholder="Digite a legenda..."
                className="min-h-[100px]"
              />
            </div>
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={generateCaptionWithAI}
                className="gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Gerar com IA
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCaptionEditModal(false)}>
              Cancelar
            </Button>
            <Button onClick={saveCaptionEdit}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Photo Modal */}
      <DialogFotos
        open={showActivityPhotoModal}
        onOpenChange={setShowActivityPhotoModal}
        onSave={handleSaveActivityPhotos}
        title={selectedActivityIndex !== null ? `Adicionar Fotos – ${rdoData?.activities?.[selectedActivityIndex]?.activity || 'Atividade'}` : 'Adicionar Fotos'}
        initialFiles={selectedActivityFiles}
        activityName={selectedActivityIndex !== null ? rdoData?.activities?.[selectedActivityIndex]?.activity : undefined}
        activityIndex={selectedActivityIndex}
      />

      {/* Occurrence Photo Modal */}
      <DialogFotos
        open={showOccurrencePhotoModal}
        onOpenChange={setShowOccurrencePhotoModal}
        onSave={handleSaveOccurrencePhotos}
        title={selectedOccurrenceIndex !== null ? `Adicionar Fotos – Ocorrência ${selectedOccurrenceIndex + 1}` : 'Adicionar Fotos'}
        initialFiles={selectedOccurrenceFiles}
        activityName={selectedOccurrenceIndex !== null ? `Ocorrência ${selectedOccurrenceIndex + 1}` : undefined}
        activityIndex={selectedOccurrenceIndex}
      />

      {/* Dialog Components */}
      <DialogAddWorkforce
        open={showWorkforceModal}
        onOpenChange={(open) => {
          setShowWorkforceModal(open);
          if (!open) {
            setEditingWorkforceIndex(null);
          }
        }}
        onAdd={handleAddWorkforce}
        editingData={editingWorkforceIndex !== null ? (
          editingWorkforceIndex === -1 ? 
            { isTeamEdit: true } : 
            rdoData?.workforce?.[editingWorkforceIndex]
        ) : undefined}
        alreadyAddedTeamNames={rdoData?.workforce?.reduce((teams: string[], item: any) => {
          // For consolidated teams (have teamName property)
          if (item.teamName && !teams.includes(item.teamName)) {
            teams.push(item.teamName);
          }
          // For individual items with team assignment
          if (item.team && item.team !== 'sem-equipe' && !teams.includes(item.team)) {
            teams.push(item.team);
          }
          return teams;
        }, []) || []}
      />

      <DialogAddEquipment
        open={showEquipmentModal}
        onOpenChange={setShowEquipmentModal}
        onAdd={handleAddEquipment}
      />

      <DialogAddMaterial
        open={showMaterialModal}
        onOpenChange={setShowMaterialModal}
        onAdd={handleAddMaterial}
      />

      <DialogFotos
        open={showPhotoModal}
        onOpenChange={setShowPhotoModal}
        onSave={handleSavePhotos}
      />

      <DialogVideos
        open={showVideoModal}
        onOpenChange={setShowVideoModal}
        onSave={handleSaveVideos}
      />

      <DialogAnexos
        open={showAttachmentModal}
        onOpenChange={setShowAttachmentModal}
        onSave={handleSaveAttachments}
      />

      {/* Gallery Modals */}
      <Dialog open={showActivityGalleryModal} onOpenChange={setShowActivityGalleryModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Fotos da Atividade {selectedActivityIndex !== null ? selectedActivityIndex + 1 : ''}
            </DialogTitle>
            <DialogDescription>
              Visualize e gerencie todas as fotos relacionadas a esta atividade.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
            {selectedActivityIndex !== null && getActivityPhotos(selectedActivityIndex).length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {getActivityPhotos(selectedActivityIndex).map((photo: any, index: number) => (
                  <div key={index} className="border rounded-lg p-2 space-y-2">
                    <div className="aspect-square bg-muted rounded overflow-hidden relative group">
                      {photo.url ? (
                        <img 
                          src={photo.url} 
                          alt={photo.caption} 
                          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      
                      {/* Overlay actions */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            const newCaption = prompt('Editar legenda:', photo.caption || '');
                            if (newCaption !== null) {
                              setRdoData(prev => prev ? {
                                ...prev,
                                photos: prev.photos?.map(p => 
                                  p.id === photo.id ? { ...p, caption: newCaption } : p
                                ) || []
                              } : prev);
                              toast({
                                title: "Legenda atualizada",
                                description: "Legenda da foto foi atualizada com sucesso."
                              });
                            }
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <FileText className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setRdoData(prev => prev ? {
                              ...prev,
                              photos: prev.photos?.filter(p => 
                                !((p as any).activityIndex === selectedActivityIndex && p.id === photo.id)
                              ) || []
                            } : prev);
                            toast({
                              title: "Foto removida",
                              description: "Foto removida da atividade com sucesso."
                            });
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground line-clamp-2" title={photo.caption}>
                        {photo.caption || 'Sem legenda'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma foto encontrada para esta atividade</p>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {selectedActivityIndex !== null ? 
                `${getActivityPhotoCount(selectedActivityIndex)} foto${getActivityPhotoCount(selectedActivityIndex) !== 1 ? 's' : ''}` : 
                '0 fotos'
              }
            </div>
            <Button onClick={() => setShowActivityGalleryModal(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showOccurrenceGalleryModal} onOpenChange={setShowOccurrenceGalleryModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Fotos da Ocorrência {selectedOccurrenceIndex !== null ? selectedOccurrenceIndex + 1 : ''}
            </DialogTitle>
            <DialogDescription>
              Visualize e gerencie todas as fotos relacionadas a esta ocorrência.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
            {selectedOccurrenceIndex !== null && getOccurrencePhotos(selectedOccurrenceIndex).length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {getOccurrencePhotos(selectedOccurrenceIndex).map((photo: any, index: number) => (
                  <div key={index} className="border rounded-lg p-2 space-y-2">
                    <div className="aspect-square bg-muted rounded overflow-hidden relative group">
                      {photo.url ? (
                        <img 
                          src={photo.url} 
                          alt={photo.caption} 
                          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setRdoData(prev => prev ? {
                              ...prev,
                              photos: prev.photos?.filter(p => 
                                !((p as any).occurrenceIndex === selectedOccurrenceIndex && p.id === photo.id)
                              ) || []
                            } : prev);
                            toast({
                              title: "Foto removida",
                              description: "Foto removida da ocorrência com sucesso."
                            });
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground line-clamp-2" title={photo.caption}>
                        {photo.caption || 'Sem legenda'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma foto encontrada para esta ocorrência</p>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setShowOccurrenceGalleryModal(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar edição</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem alterações não salvas. Tem certeza que deseja cancelar? Todas as alterações serão perdidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancelEditing}>
              Sim, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Export wrapped component with error boundary
export default function RDOWrapper() {
  return (
    <ErrorBoundary>
      <RDO />
    </ErrorBoundary>
  );
}
